<template>
  <li>
    <div class="category-item">
      <span class="category-element" v-if="node.children && node.children.length"
            @click="toggleOpen"
      >
        {{ isOpen ? '-' : '+' }}
      </span>

      <input type="checkbox"
             :checked="isAllSelected"
             :indeterminate="isPartialSelected"
             @change="toggleSelectNode"
             style="margin-right:4px;"
      />

      <span class="category-name"
            v-html="highlightLabel(node.label)"
            @click="toggleSelectNode"
      />
      <span class="category-path"
            v-html="node.category_str_path"
            @click="toggleSelectNode"
      />
    </div>

    <ul v-show="isOpen" style="list-style:none; padding-left:1.5em;">
      <TreeNode
          v-for="child in node.children || []"
          :key="child.id"
          :node="child"
          :rootNodes="rootNodes"
          :selectedKeys="selectedKeys"
          :openKeys="openKeys"
          :parentMap="parentMap"
          :filter-text="filterText"
          @change-selection="$emit('change-selection', $event)"
          @change-open-keys="$emit('change-open-keys', $event)"
      />
    </ul>
  </li>
</template>

<script setup>
import {computed} from 'vue';

const props = defineProps({
  node: Object,
  rootNodes: Array,
  selectedKeys: Object,
  openKeys: Object,
  parentMap: Object,
  filterText: String
});

const emit = defineEmits(['change-selection', 'change-open-keys']);

const isOpen = computed({
  get: () => !!props.openKeys[props.node.id],
  set: (val) => {
    const newKeys = {...props.openKeys};

    if (val) {
      newKeys[props.node.id] = true;
    } else {
      delete newKeys[props.node.id];
    }

    emit('change-open-keys', newKeys);
  }
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const isAllSelected = computed(() => {
  return !!props.selectedKeys[props.node.id];
});

const isPartialSelected = computed(() => {
  const traverse = (n) => {
    if (!n.children || n.children.length === 0) {
      return !!props.selectedKeys[n.id];
    }

    const childSome = n.children.some(traverse);

    return (!!props.selectedKeys[n.id] && !childSome) || childSome;
  };

  return traverse(props.node) && !isAllSelected.value;
});

const toggleSelectNode = () => {
  const newKeys = {...props.selectedKeys};
  const select = !isAllSelected.value;

  const traverse = (n) => {
    if (select) {
      newKeys[n.id] = true;
    } else {
      delete newKeys[n.id];
    }
    n.children?.forEach(traverse);
  };

  const findFullNode = (nodes, targetId) => {
    for (const n of nodes) {
      if (n.id === targetId) {
        return n;
      }
      if (n.children) {
        const found = findFullNode(n.children, targetId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const fullNode = findFullNode(props.rootNodes, props.node.id);
  if (fullNode) {
    traverse(fullNode);
  }

  const updateParents = (n) => {
    const parent = props.parentMap[n.id];
    if (!parent) {
      return;
    }
    const allChildSelected = parent.children.every(c => newKeys[c.id]);
    if (allChildSelected) {
      newKeys[parent.id] = true;
    }
    updateParents(parent);
  };

  updateParents(props.node);
  emit('change-selection', newKeys);
};

const highlightLabel = (label) => {
  if (!props.filterText) {
    return label;
  }

  const regex = new RegExp(`(${props.filterText})`, 'gi');

  return label.replace(regex, '<span style="background:yellow;">$1</span>');
};
</script>

<style scoped src="./css/style.css"/>
